import { UPLOAD_PURPOSE_SCRIPT_REVIEW } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_UPLOAD_MS } from "@/lib/query-constants";
// The XHR-progress PUT is the shared upload helper (auth + marketplace use it too).
import { putToSignedUrlWithProgress } from "@/features/preflight/api/uploads.api";

// ─── Upload flow (mirrors preflight, with the SCRIPT_REVIEW purpose) ────────

export interface UploadPresignResult {
  uploadId: string;
  bucket: string;
  objectKey: string;
  uploadUrl: string;
  expiresAt: string;
  requiredHeaders: Record<string, string>;
  requiresMemoryStage: boolean;
  requiresServerStage: boolean;
}

export interface UploadConfirmResult {
  uploadId: string;
  status: string;
  contentType: string | null;
  sizeBytes: number | null;
  checksumMd5: string | null;
}

async function presign(input: {
  tenantId: string;
  purpose: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: input.purpose,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

async function stageMemory(input: {
  tenantId: string;
  uploadId: string;
  contentType: string;
  file: File;
}): Promise<void> {
  const form = new FormData();
  form.append("tenantId", input.tenantId);
  form.append("uploadId", input.uploadId);
  form.append("contentType", input.contentType);
  form.append("file", input.file, input.file.name);
  await authFetch("/uploads/stage-memory", {
    method: "POST",
    body: form,
    timeoutMs: API_REQUEST_TIMEOUT_UPLOAD_MS,
  });
}

async function confirm(input: { tenantId: string; uploadId: string }): Promise<UploadConfirmResult> {
  const { data } = await authFetch<UploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

/**
 * presign → upload (or stage) → confirm. Returns the confirmed upload id.
 * Defaults to the video purpose; pass `purpose` (+ an optional normalized
 * `contentType`) to upload a script FILE instead.
 *
 * `onProgress` reports 0-100 for the direct-to-storage PUT. The stage-memory
 * fallback (local/dev, and server-staged purposes) can't report progress — it
 * jumps straight to 100 on completion, so callers should treat progress as a
 * hint, not a guarantee.
 */
export async function runScriptReviewUploadFlow(input: {
  tenantId: string;
  file: File;
  purpose?: string;
  contentType?: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}): Promise<string> {
  const purpose = input.purpose ?? UPLOAD_PURPOSE_SCRIPT_REVIEW;
  const contentType = input.contentType || input.file.type;
  const p = await presign({
    tenantId: input.tenantId,
    purpose,
    contentType,
    sizeBytes: input.file.size,
    originalName: input.file.name,
  });
  if (p.requiresMemoryStage || p.requiresServerStage) {
    await stageMemory({
      tenantId: input.tenantId,
      uploadId: p.uploadId,
      contentType,
      file: input.file,
    });
    input.onProgress?.(100);
  } else {
    await putToSignedUrlWithProgress(p.uploadUrl, input.file, p.requiredHeaders, {
      onProgress: input.onProgress,
      signal: input.signal,
    });
  }
  await confirm({ tenantId: input.tenantId, uploadId: p.uploadId });
  return p.uploadId;
}

/**
 * Ask the AI which script in `setId` the uploaded video was made from. Returns a
 * correlationId to poll — transcription is far too slow for one request.
 */
export async function matchScriptForSet(input: {
  tenantId: string;
  uploadId: string;
  setId: string;
}): Promise<{ correlationId: string }> {
  // The 202 carries correlationId at the top level, not inside `data`.
  const { correlationId } = await authFetch<void>("/script-reviews/match-script", {
    method: "POST",
    body: JSON.stringify({
      ...input,
      // Same video + same set = the same question; no need to re-ask.
      idempotencyKey: `${input.uploadId}:${input.setId}`,
    }),
  });
  if (!correlationId) throw new Error("Script match response missing correlationId");
  return { correlationId };
}

// ─── Review API ─────────────────────────────────────────────────────────────

/** The auto-match verdict, as the form's status poll returns it. */
export interface ScriptMatchResult {
  matched: boolean;
  /** Only set when `matched` — always one of the set's own script ids. */
  scriptId?: string;
  confidence?: number;
  reasoning?: string;
}

export type ScriptReviewStatus = "QUEUED" | "ANALYZING" | "COMPLETED" | "FAILED";
export type ScriptReviewCommentType = "VOICEOVER" | "TEXT_OVERLAY" | "VISUAL";
/** What the video was judged against: a script's scenes, a reference video + brief, or nothing (CLEAN). */
export type ScriptReviewMode = "SCRIPT" | "ITERATION" | "CLEAN";

export function isTerminalScriptReviewStatus(status: ScriptReviewStatus | undefined): boolean {
  return status === "COMPLETED" || status === "FAILED";
}

export interface ScriptReviewSummary {
  id: string;
  status: ScriptReviewStatus;
  /** SCRIPT compares against a script; ITERATION against a reference video + brief; CLEAN stores the video with no comparison. */
  mode: ScriptReviewMode;
  /** Set-script path (null when a custom script file was uploaded). */
  opportunitySetId: string | null;
  scriptId: string | null;
  /** Custom-script path: the uploaded script file. */
  scriptUploadId: string | null;
  videoUploadId: string;
  /** 1 for an original review; increments per uploaded video version. */
  version: number;
  /** Set once an admin signed this version off as ready to show the client. */
  readyForClientAt: string | null;
  error: string | null;
  /** Comment-pass names that failed after retries (`voiceover` / `text-overlay` / `visual`). */
  failedPasses: string[];
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Server-computed readiness score — the client renders it, never decides it. */
export interface ScriptReviewReadiness {
  /** Standalone human notes (replies excluded) — all must be resolved. */
  humanTotal: number;
  humanResolved: number;
  aiTotal: number;
  aiResolved: number;
  aiResolvedPct: number;
  thresholdPct: number;
  isEligible: boolean;
}

/** One entry in a review's version family, for the report-page switcher. */
export interface ScriptReviewVersion {
  id: string;
  version: number;
  status: ScriptReviewStatus;
  createdAt: string;
}

export interface ScriptReviewComment {
  id: string;
  type: ScriptReviewCommentType;
  text: string;
  exactSecond: number;
  sceneNumber: number | null;
  severity: string | null;
  /** Set when the thread was marked addressed (readiness scoring). */
  resolvedAt: string | null;
  resolvedByName: string | null;
}

export type ScriptReviewNoteAuthorKind = "APP_USER" | "GUEST";

/** A human collaboration note (in-app or guest), attributed by an entered name. */
export interface ScriptReviewNote {
  id: string;
  aiCommentId: string | null;
  anchorSecond: number | null;
  authorName: string;
  authorKind: ScriptReviewNoteAuthorKind;
  body: string;
  /** Only standalone notes are resolvable; replies resolve with their thread. */
  resolvedAt: string | null;
  resolvedByName: string | null;
  createdAt: string;
}

/** Where a new note anchors: a reply to an AI comment, or a standalone second. */
export type PostNoteTarget = { aiCommentId: string } | { anchorSecond: number };

/** Client org reviewers may only post standalone timeline notes. */
export function toClientOrgNotePayload(
  name: string,
  body: string,
  target: PostNoteTarget,
): { name: string; body: string; anchorSecond: number } {
  return {
    name,
    body,
    anchorSecond: "anchorSecond" in target ? target.anchorSecond : 0,
  };
}

/** Step 0 (transcript) output — one entry per transcribed segment. */
export interface ScriptReviewTranscriptSegment {
  startSecond: number;
  endSecond: number;
  text: string;
}

export interface ScriptReviewTranscript {
  segments: ScriptReviewTranscriptSegment[];
}

/** A resolved script scene the video was compared against (set script or parsed upload). */
export interface ScriptReviewScene {
  sceneNumber?: number;
  sceneName: string;
  hearing: string;
  seeing: string;
  props: string;
}

export interface ScriptReviewScenes {
  scenes: ScriptReviewScene[];
}

export interface ScriptReviewDetail extends ScriptReviewSummary {
  /** Step 0 output, persisted on the review. Null on older/failed reviews. */
  transcript: ScriptReviewTranscript | null;
  /** Scenes actually compared against (from the set script or the parsed upload). */
  scriptScenes: ScriptReviewScenes | null;
  /** Iteration mode: the change brief the vendor was given. Null in script mode. */
  brief: string | null;
  /** Iteration mode: the reference video's upload id. Null in script mode. */
  referenceVideoUploadId: string | null;
  /** Iteration mode: short-lived signed URL for the reference video. */
  referenceVideoUrl: string | null;
  /** Short-lived signed video URL for the timeline player. Null if unavailable. */
  videoUrl: string | null;
  /** Public share token, or null if not shared / revoked. */
  shareToken: string | null;
  /** Predecessor version's id (null for the original). */
  previousReviewId: string | null;
  /** True only for the newest version — only the latest can spawn a new version. */
  isLatest: boolean;
  /** Every version in this family (newest first) for the switcher dropdown. */
  versions: ScriptReviewVersion[];
  /** Server-computed readiness for THIS version. */
  readiness: ScriptReviewReadiness;
  comments: ScriptReviewComment[];
  notes: ScriptReviewNote[];
}

export interface ScriptReviewStatusResult {
  id: string;
  status: ScriptReviewStatus;
  commentCount: number;
  error: string | null;
}

export async function createScriptReview(input: {
  uploadId: string;
  setId?: string;
  scriptId?: string;
  scriptUploadId?: string;
  /** Iteration path: the reference video's upload id — pass together with `brief`. */
  referenceUploadId?: string;
  /** Iteration path: the change brief the vendor was given. */
  brief?: string;
  /** Pass alone (with a new video uploadId) to create the next version of a review. */
  previousReviewId?: string;
  /** Pass alone (with uploadId) to store the video with no AI review. */
  mode?: "CLEAN";
}): Promise<{ reviewId: string }> {
  const { data } = await authFetch<{ reviewId: string }>("/script-reviews", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function getScriptReviewStatus(id: string): Promise<ScriptReviewStatusResult> {
  const { data } = await authFetch<ScriptReviewStatusResult>(`/script-reviews/${id}/status`);
  return data;
}

export async function getScriptReview(id: string): Promise<ScriptReviewDetail> {
  const { data } = await authFetch<ScriptReviewDetail>(`/script-reviews/${id}`);
  return data;
}

/** Add a collaboration note as the in-app (authenticated) user. */
export async function postScriptReviewNote(
  reviewId: string,
  input: { name: string; body: string } & PostNoteTarget,
): Promise<ScriptReviewNote> {
  const { data } = await authFetch<ScriptReviewNote>(`/script-reviews/${reviewId}/notes`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

/** Generate (or rotate) the public share token for a review. */
export async function createScriptReviewShareLink(reviewId: string): Promise<{ shareToken: string }> {
  const { data } = await authFetch<{ shareToken: string }>(`/script-reviews/${reviewId}/share`, {
    method: "POST",
  });
  return data;
}

/** Revoke the public share token. */
export async function revokeScriptReviewShareLink(reviewId: string): Promise<void> {
  await authFetch(`/script-reviews/${reviewId}/share`, { method: "DELETE" });
}

/**
 * Adge-admin moderation: permanently delete an AI comment (its replies cascade).
 * A `reason` additionally spawns a prompt-improvement ticket server-side.
 */
export async function deleteScriptReviewComment(
  reviewId: string,
  commentId: string,
  reason?: string,
): Promise<void> {
  await authFetch(
    `/script-reviews/${reviewId}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE", ...(reason ? { body: JSON.stringify({ reason }) } : {}) },
  );
}

/** Adge-admin moderation: permanently delete a human note (reply or standalone). */
export async function deleteScriptReviewNote(
  reviewId: string,
  noteId: string,
): Promise<void> {
  await authFetch(
    `/script-reviews/${reviewId}/notes/${encodeURIComponent(noteId)}`,
    { method: "DELETE" },
  );
}

/** `ai` keeps standalone team notes; `all` clears the version's whole timeline. */
export type ScriptReviewBulkDeleteScope = "ai" | "all";

export interface ScriptReviewBulkDeleteResult {
  aiComments: number;
  notes: number;
}

/** Adge-admin moderation: clear one version's timeline in a single all-or-nothing delete. */
export async function deleteScriptReviewComments(
  reviewId: string,
  scope: ScriptReviewBulkDeleteScope,
): Promise<ScriptReviewBulkDeleteResult> {
  const { data } = await authFetch<ScriptReviewBulkDeleteResult>(
    `/script-reviews/${reviewId}/comments?scope=${scope}`,
    { method: "DELETE" },
  );
  return data;
}

/** Readiness: mark an AI comment's thread resolved / unresolved. */
export async function setScriptReviewCommentResolution(
  reviewId: string,
  commentId: string,
  resolved: boolean,
): Promise<void> {
  await authFetch(
    `/script-reviews/${reviewId}/comments/${encodeURIComponent(commentId)}/resolution`,
    { method: "PATCH", body: JSON.stringify({ resolved }) },
  );
}

/** Readiness: mark a standalone human note resolved / unresolved. */
export async function setScriptReviewNoteResolution(
  reviewId: string,
  noteId: string,
  resolved: boolean,
): Promise<void> {
  await authFetch(
    `/script-reviews/${reviewId}/notes/${encodeURIComponent(noteId)}/resolution`,
    { method: "PATCH", body: JSON.stringify({ resolved }) },
  );
}

/** Readiness: flip the client-review sign-off (server re-validates the criteria). */
export async function setScriptReviewReady(
  reviewId: string,
  ready: boolean,
): Promise<{ readyForClientAt: string | null; readiness: ScriptReviewReadiness }> {
  const { data } = await authFetch<{
    readyForClientAt: string | null;
    readiness: ScriptReviewReadiness;
  }>(`/script-reviews/${reviewId}/ready`, {
    method: "PATCH",
    body: JSON.stringify({ ready }),
  });
  return data;
}

export async function listScriptReviews(params: {
  cursor?: string;
  limit?: number;
}): Promise<{ items: ScriptReviewSummary[]; nextCursor: string | null; hasMore: boolean }> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const { data } = await authFetch<{
    items: ScriptReviewSummary[];
    nextCursor: string | null;
    hasMore: boolean;
  }>(`/script-reviews${suffix}`);
  return data;
}
